import { technicalReportInfo, type TechnicalReportId } from "./technicalReports/catalog";

export type PdfApplicationMode = "main" | "c1";
export type PdfChapterId =
  | TechnicalReportId
  | "floorPlans"
  | "roomPlans"
  | "roombook"
  | "buttonList"
  | "billOfMaterials"
  | "groupAddresses"
  | "cabinetLayout"
  | "fuseList";
export type PdfPlanMode = "design" | "technical" | "din";
export type PdfCabinetMode = "schematic" | "graphic" | "v1" | "v2";
export type PdfFuseSort = "consumer" | "actorChannel" | "terminal";
export type PdfTerminalSpacing = "compact" | "normal" | "wide";
export type PdfChapterConfig = {
  id: PdfChapterId;
  enabled: boolean;
  roombookMode?: "functions" | "groups";
  planMode?: PdfPlanMode;
  /** Export-only symbol zoom, independent for each plan chapter. */
  eloItemScale?: number;
  showTags?: boolean;
  includeDimensions?: boolean;
  detailedGroupAddressUsage?: boolean;
  fuseMode?: "list" | "plan";
  fuseSort?: PdfFuseSort;
  cabinetMode?: PdfCabinetMode;
  cabinetRailMode?: PdfCabinetMode | "off";
  cablePullCounts?: boolean;
  cablePullPagePerRoom?: boolean;
  consumerGroupByRoom?: boolean;
  actorShowImage?: boolean;
  pricesPerCabinet?: boolean;
  terminalSpacing?: PdfTerminalSpacing;
  terminalMode?: "list" | "plan";
  terminalShowUnusedWires?: boolean;
  terminalViaExplanation?: boolean;
  billOfMaterialsMode?: "total" | "floors";
  billOfMaterialsUsage?: boolean;
  billOfMaterialsLightTypes?: boolean;
  billOfMaterialsWithoutProducts?: boolean;
};
export type PdfExportConfig = { chapters: PdfChapterConfig[]; useTags?: boolean; separateTagAndName?: boolean };
export type PdfExportHandler = (config: PdfExportConfig, mode: PdfApplicationMode, singleChapterId?: PdfChapterId) => Promise<void>;

export function isPdfChapterAvailable(chapter: Pick<PdfChapterConfig, "id">,
  app: { allScenes?: () => Array<{ isDeleted?: boolean; isMultiplexer?: boolean }> }): boolean {
  return chapter.id !== "scenes" || !!app.allScenes?.().some(scene => !scene.isDeleted && !scene.isMultiplexer);
}

export function availablePdfConfig(config: PdfExportConfig,
  app: Parameters<typeof isPdfChapterAvailable>[1]): PdfExportConfig {
  return { ...config, chapters: config.chapters.map(chapter => ({ ...chapter,
    enabled: chapter.enabled && isPdfChapterAvailable(chapter, app) })) };
}

/** Spreadsheet documentation contains list chapters; drawings have no tabular equivalent. */
export function isExcelChapter(chapter: PdfChapterConfig): boolean {
  return !["floorPlans", "roomPlans", "cabinetLayout"].includes(chapter.id);
}

export function excelChapter(chapter: PdfChapterConfig): PdfChapterConfig {
  return { ...chapter, ...(chapter.id === "terminals" ? { terminalMode: "list" as const } : {}),
    ...(chapter.id === "fuseList" ? { fuseMode: "list" as const } : {}) };
}

/** Restricts only the current export; the saved chapter selection remains unchanged. */
export function selectPdfChapter(config: PdfExportConfig, id?: PdfChapterId): PdfExportConfig {
  if (!id) return config;
  if (!config.chapters.some(chapter => chapter.id === id)) throw new Error("Unbekanntes PDF-Kapitel.");
  return { ...config, chapters: config.chapters.map(chapter => ({ ...chapter, enabled: chapter.id === id })) };
}

export const pdfChapterInfo: Record<PdfChapterId, { title: string; description: string }> = {
  ...technicalReportInfo,
  cabinetLayout: { title: "Schaltschranklayout", description: "Schaltschrank in A4 hochkant, einzelne Schienen in A4 quer" },
  fuseList: { title: "Sicherungsliste", description: "FI, Sicherungen und Verbraucher als Liste oder Stromlaufplan" },
  floorPlans: { title: "Etagen-Grundrisse", description: "Alle Etagen als Übersichtspläne" },
  roomPlans: { title: "Raumgrundrisse", description: "Pro Raum ein einzelner Grundriss" },
  roombook: { title: "Raumbuch", description: "Ausstattung und Funktionen je Raum" },
  buttonList: { title: "Taster-Liste", description: "Detaillierte Dokumentation für jeden Taster" },
  billOfMaterials: { title: "Stückliste", description: "Geräte und Betriebsmittel zusammenfassen" },
  groupAddresses: {
    title: "Gruppenadressen",
    description: "Hauptgruppen, Mittelgruppen und Gruppenadressen mit Verwendungen",
  },
};

/** Keeps chapter availability and defaults separate for each application. */
export function defaultPdfChapters(mode: PdfApplicationMode): PdfChapterConfig[] {
  const chapters: PdfChapterConfig[] = [
    { id: "floorPlans", enabled: true, planMode: "design" },
    { id: "roomPlans", enabled: true, planMode: "design", showTags: true },
    { id: "roombook", enabled: true, roombookMode: "functions" },
    { id: "buttonList", enabled: true },
    {
      id: "billOfMaterials",
      enabled: true,
      billOfMaterialsMode: "total",
      billOfMaterialsUsage: false,
      billOfMaterialsLightTypes: false,
      billOfMaterialsWithoutProducts: true,
    },
  ];
  if (mode === "main") {
    chapters.push({ id: "cabinetLayout", enabled: false, cabinetMode: "schematic", cabinetRailMode: "off" });
    chapters.push({ id: "groupAddresses", enabled: true });
    chapters.push({ id: "fuseList", enabled: false, fuseMode: "list", fuseSort: "consumer" });
    chapters.push({ id: "terminals", enabled: false, terminalMode: "plan", terminalSpacing: "normal", terminalShowUnusedWires: false });
    for (const id of Object.keys(technicalReportInfo) as TechnicalReportId[]) {
      if (id === "terminals") continue;
      chapters.push({ id, enabled: false, ...(id === "cablePull" ? { cablePullCounts: true, cablePullPagePerRoom: false } : {}),
        ...(id === "actorList" ? { actorShowImage: true } : {}),
        ...(id === "consumerConnections" ? { consumerGroupByRoom: true } : {}),
        ...(id === "productPrices" ? { pricesPerCabinet: false } : {}) });
    }
  }
  return chapters;
}

/** Uses the selected representation consistently in navigation and document headings. */
export function pdfChapterTitle(chapter: PdfChapterConfig): string {
  if (chapter.id === "terminals" && chapter.terminalMode === "list") return "Klemmenliste";
  if (chapter.id === "fuseList" && chapter.fuseMode === "plan") return "Sicherungsplan";
  return pdfChapterInfo[chapter.id].title;
}

/** Restores order and options, excluding chapters unavailable in this application. */
export function restorePdfConfig(mode: PdfApplicationMode, stored?: PdfExportConfig, isC1Project = true): PdfExportConfig {
  const remaining = new Map(defaultPdfChapters(mode).map((chapter) => [chapter.id, chapter]));
  const chapters: PdfChapterConfig[] = [];
  for (const chapter of stored?.chapters ?? []) {
    const fallback = remaining.get(chapter.id);
    if (!fallback) continue;
    chapters.push({ ...fallback, ...chapter });
    remaining.delete(chapter.id);
  }
  chapters.push(...remaining.values());
  for (const chapter of chapters) {
    if (chapter.id === "floorPlans" || chapter.id === "roomPlans") {
      if (!["design", "technical", "din"].includes(chapter.planMode ?? "")) chapter.planMode = "design";
      chapter.eloItemScale = typeof chapter.eloItemScale === "number" && Number.isFinite(chapter.eloItemScale)
        ? Math.min(2, Math.max(0.5, chapter.eloItemScale)) : 1;
    }
    if (chapter.id === "terminals" && chapter.terminalMode !== "list") chapter.terminalMode = "plan";
    if (chapter.id === "terminals" && !["compact", "normal", "wide"].includes(chapter.terminalSpacing ?? "")) chapter.terminalSpacing = "normal";
    if (chapter.id === "cabinetLayout") {
      if (!["schematic", "graphic", "v1", "v2"].includes(chapter.cabinetMode ?? "")) chapter.cabinetMode = "schematic";
      if (!["off", "schematic", "graphic", "v1", "v2"].includes(chapter.cabinetRailMode ?? "")) chapter.cabinetRailMode = "off";
    }
    if (!isC1Project && chapter.id === "roombook") chapter.roombookMode = "functions";
  }
  return { chapters, useTags: stored?.useTags ?? true, separateTagAndName: stored?.separateTagAndName ?? true };
}
